/**
 * SF Field Metadata Service
 *
 * Provides field type information, picklist values, and lookup targets.
 *
 * Source: Gobodo field definitions only (runtime, via getObjectTypeFields).
 *
 * The sync script (scripts/sync-sf-field-metadata-to-gobodo.ts) pushes
 * SF metadata into Gobodo. This service reads SkinField metadata from Gobodo only.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface SfFieldMeta {
  label: string;
  type: string;        // UI type: select, lookup, currency, phone, email, date, datetime, checkbox, textarea, text, etc.
  sfType: string;      // Raw SF type or Gobodo type
  picklistValues?: string[];
  referenceTo?: string[];
  relationshipName?: string;
  updateable: boolean;
  required: boolean;
  length?: number;
  precision?: number;
  scale?: number;
}

type ObjectFieldMap = Record<string, SfFieldMeta>;

// ---------------------------------------------------------------------------
// Gobodo type → UI type mapping
// ---------------------------------------------------------------------------
function gobodoTypeToUiType(gobodoType: string): string {
  switch (gobodoType) {
    case 'select':
    case 'enum-select':
      return 'select';
    case 'multiple-select':
    case 'enum-multiple-select':
    case 'multipicklist':
      return 'multipicklist';
    case 'relationship':
    case 'relationship-select':
    case 'reverse-relationship':
    case 'relationship-or-text':
    case 'object':
      return 'lookup';
    case 'relationship-multiple':
    case 'relationship-multiple-select':
    case 'multi-object':
      return 'lookup-multi';
    case 'numeric':
    case 'decimal':
    case 'range':
      return 'number';
    case 'currency': return 'currency';
    case 'percentage': return 'percent';
    case 'phone': return 'phone';
    case 'email': return 'email';
    case 'date':
    case 'daterange':
      return 'date';
    case 'datetime':
    case 'datetimerange':
      return 'datetime';
    case 'checkbox':
    case 'switch':
      return 'checkbox';
    case 'radiobutton': return 'radio';
    case 'textarea':
    case 'multiple-text':
      return 'textarea';
    case 'wysiwyg': return 'richtextarea';
    case 'url': return 'url';
    case 'file':
    case 'multi-file':
      return 'file';
    case 'image':
    case 'multi-image':
      return 'image';
    default: return 'text';
  }
}

function toStringArray(input: unknown): string[] {
  if (Array.isArray(input)) {
    return input
      .map((v) => {
        if (v == null) return '';
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
        if (typeof v === 'object') {
          const rec = v as Record<string, unknown>;
          return String(rec.value ?? rec.label ?? rec.name ?? rec.id ?? '').trim();
        }
        return '';
      })
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return [];
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try {
        return toStringArray(JSON.parse(s));
      } catch {
        return [];
      }
    }
    return [s];
  }
  if (input && typeof input === 'object') {
    const rec = input as Record<string, unknown>;
    return (
      toStringArray(rec.enumValues) ||
      toStringArray(rec.picklistValues) ||
      toStringArray(rec.values) ||
      toStringArray(rec.options) ||
      toStringArray(rec.sfFieldInput) ||
      []
    );
  }
  return [];
}

function extractPicklistValues(field: Record<string, unknown>): string[] {
  const direct = [
    ...toStringArray(field.enumValues),
    ...toStringArray(field.picklistValues),
    ...toStringArray(field.values),
    ...toStringArray(field.options),
    ...toStringArray(field.sfFieldInput),
  ];
  const uniq = Array.from(new Set(direct.map((v) => String(v).trim()).filter(Boolean)));
  return uniq;
}

// ---------------------------------------------------------------------------
// Runtime cache (populated from Gobodo on first access per object type)
// ---------------------------------------------------------------------------
const runtimeCache: Record<string, ObjectFieldMap> = {};
const fetchPromises: Record<string, Promise<ObjectFieldMap | null>> = {};

/** SF object name → Gobodo object type key mapping */
const SF_TO_GOBODO_KEY: Record<string, string> = {};

// Build mappings dynamically from the custom suffix
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const OBJECT_MAPPINGS: Array<[string, string]> = [
  ['Account', `Client${CUSTOM_SUFFIX}`],
  ['Case', `Case${CUSTOM_SUFFIX}`],
  ['Contact', `Contact${CUSTOM_SUFFIX}`],
  ['Task', `Task${CUSTOM_SUFFIX}`],
  ['CreditBlock__c', `CreditBlock${CUSTOM_SUFFIX}`],
  ['Credit_Bureaus__c', `Credit_Bureaus${CUSTOM_SUFFIX}`],
  ['Unlock_Request__c', `Unlock_Request${CUSTOM_SUFFIX}`],
  ['Credit_Report__c', `CreditReport${CUSTOM_SUFFIX}`],
];
for (const [sf, gobodo] of OBJECT_MAPPINGS) {
  SF_TO_GOBODO_KEY[sf] = gobodo;
}

/**
 * Fetch field definitions for a Gobodo object type and cache them.
 * Returns null if fetch fails.
 */
async function fetchFieldsFromGobodo(gobodoKey: string): Promise<ObjectFieldMap | null> {
  try {
    const GobodoApi = await import('@gobodo/gobodo-api-promise');
    const { getGobodoClient } = await import('../gobodo/gobodoClient');
    const client = getGobodoClient();
    const typesApi = new GobodoApi.GraphObjectTypesApi(client);

    // Get the object type ID first
    const objectType = await typesApi.getObjectTypeByKey(gobodoKey);
    if (!objectType?.id) return null;

    // Fetch all fields for this object type (the API defaults to a partial page).
    const size = 200;
    const maxPages = 100;
    const fields: any[] = [];
    for (let page = 0; page < maxPages; page++) {
      const rows = await (typesApi as any).getObjectTypeFields(objectType.id, { page, size });
      const batch = Array.isArray(rows) ? rows : [];
      if (batch.length === 0) break;
      fields.push(...batch);
      if (batch.length < size) break;
    }
    if (!Array.isArray(fields) || fields.length === 0) return null;

    const fieldMap: ObjectFieldMap = {};
    for (const f of fields) {
      const uiType = gobodoTypeToUiType(f.type || 'text');
      const meta: SfFieldMeta = {
        label: f.label || f.name || '',
        type: uiType,
        sfType: f.type || 'text',
        updateable: !f.isReadOnly,
        required: f.required || false,
      };

      // Picklist values can be stored across several Gobodo field properties
      // depending on sync source and server version. Attach them for any
      // picklist-shaped UI type — single (select) or multi (multipicklist) —
      // so the form can render the correct options regardless of cardinality.
      const values = extractPicklistValues(f as Record<string, unknown>);
      if (
        values.length > 0 &&
        (uiType === 'select' ||
          uiType === 'multipicklist' ||
          f.type === 'select' ||
          f.type === 'multiple-select' ||
          f.type === 'enum-select' ||
          f.type === 'enum-multiple-select')
      ) {
        meta.picklistValues = values;
      }

      // Lookup info
      if (f.type === 'relationship' && f.relTypeId) {
        meta.referenceTo = []; // Would need relationship type lookup to resolve target
      }

      fieldMap[f.name] = meta;
    }

    return fieldMap;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[sfFieldMetadata] Failed to fetch fields for ${gobodoKey}:`, err);
    }
    return null;
  }
}

/**
 * Get fields for an object from Gobodo runtime cache.
 */
function getFieldsForObject(objectName: string): ObjectFieldMap {
  const gobodoKey = SF_TO_GOBODO_KEY[objectName];

  // Check runtime cache first
  if (gobodoKey && runtimeCache[gobodoKey]) {
    return runtimeCache[gobodoKey];
  }

  // Trigger async fetch from Gobodo (non-blocking, populates cache for next call)
  if (gobodoKey && !fetchPromises[gobodoKey]) {
    fetchPromises[gobodoKey] = fetchFieldsFromGobodo(gobodoKey).then(result => {
      if (result) {
        runtimeCache[gobodoKey] = result;
      }
      return result;
    });
  }

  return {};
}

// ---------------------------------------------------------------------------
// Field name aliases (our camelCase → SF API name)
// ---------------------------------------------------------------------------

/** Manual aliases for fieldNames that don't match SF API name conventions */
const FIELD_ALIASES: Record<string, string> = {
  // Account fields
  accountOwner: 'OwnerId',
  accountName: 'Name',
  clientName: 'Name',
  email: 'PersonEmail',
  mobile: 'PersonMobilePhone',
  dateOfBirth: 'Birthday__c',
  socialSecurityNumber: 'SSN__c',
  creditReportFlag: 'Free_Credit_Report_2__c',
  managingFirm: 'Service_Management__c',
  accountManagerCompany: 'Account_Manager_Company__c',
  accountManager: 'Contact__c',
  dayToDayContact: 'Daily_Contact__c',
  referralCompany: 'ReferralCompany__c',
  referralPartnerContact: 'Referral_Contact__c',
  referralPersonContact: 'Person_Referral__c',
  recordCreatedBy: 'CreatedById',
  lastModifiedBy: 'LastModifiedById',
  createdDate: 'CreatedDate',
  lastModifiedDate: 'LastModifiedDate',
  emailCCs: 'Email_CC_s__c',
  teamEmail: 'Regal_Team_Email__c',
  regalTeamEmail: 'Regal_Team_Email__c',
  // CreditBlock__c fields
  creditProgramType: 'Credit_Program_Type__c',
  creditBlockStage: 'Stage__c',
  initialLockdownDate: 'Initial_Lockdown_Date__c',
  optOutPrescreenStatus: 'Opt_out_Prescreen_Status__c',
  optOutPrescreenStartDate: 'Opt_out_Prescreen_Start_Date__c',
  // Credit_Bureaus__c fields
  bureauStage: 'Stage__c',
  bureauAuthority: 'Authority__c',
  // Unlock_Request__c fields
  requestType: 'Request_Type__c',
  requestSource: 'Source__c',
  requestStatus: 'Status__c',
  // Credit_Report__c fields
  creditReportStatus: 'Credit_Report_Status__c',
  creditReportPullDate: 'Credit_Report_Pull_Date__c',
  creditRepositoryIncluded: 'CreditRepositoryIncluded__c',
  bureau: 'Bureau__c',
  rating: 'Rating__c',
  grade: 'grade__c',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Get field metadata for a specific field on an object.
 * Accepts SF API name (Bulk_SMS_Status__c) or camelCase (bulkSmsStatus).
 */
export function getFieldMeta(objectName: string, fieldName: string): SfFieldMeta | null {
  const fields = getFieldsForObject(objectName);
  const normalize = (s: string) =>
    String(s ?? '')
      .replace(/__[cpr]$/i, '')
      .replace(/[^a-z0-9]/gi, '')
      .toLowerCase();
  const findInsensitive = (name: string): SfFieldMeta | null => {
    const target = String(name ?? '').trim();
    if (!target) return null;
    const direct = fields[target];
    if (direct) return direct;
    const lowerTarget = target.toLowerCase();
    for (const [k, v] of Object.entries(fields)) {
      if (k.toLowerCase() === lowerTarget) return v;
    }
    const normTarget = normalize(target);
    for (const [k, v] of Object.entries(fields)) {
      if (normalize(k) === normTarget) return v;
    }
    return null;
  };

  // Direct match
  const matched = findInsensitive(fieldName);
  if (matched) return matched;

  // Check manual alias
  const alias = FIELD_ALIASES[fieldName];
  if (alias) {
    const byAlias = findInsensitive(alias);
    if (byAlias) return byAlias;
  }

  return null;
}

/**
 * Get all field metadata for an object.
 */
export function getObjectFieldMeta(objectName: string): ObjectFieldMap | null {
  const fields = getFieldsForObject(objectName);
  return Object.keys(fields).length > 0 ? fields : null;
}

/**
 * Get picklist values for a field. Returns null if not a picklist.
 */
export function getPicklistValues(objectName: string, fieldName: string): string[] | null {
  const meta = getFieldMeta(objectName, fieldName);
  if (!meta || !meta.picklistValues) return null;
  return meta.picklistValues;
}

/**
 * Get the UI field type for a field (select, lookup, currency, phone, etc.)
 */
export function getFieldType(objectName: string, fieldName: string): string {
  const meta = getFieldMeta(objectName, fieldName);
  return meta?.type ?? 'text';
}

/**
 * Get picklist options in the format DynamicFormSection expects.
 */
export function getPicklistOptions(objectName: string, fieldName: string): Array<{ label: string; value: string }> | null {
  const values = getPicklistValues(objectName, fieldName);
  if (!values) return null;
  return values.map(v => ({ label: v, value: v }));
}

/**
 * Prefetch field metadata for an object type from Gobodo.
 * Call this early (e.g. on page load) so fields are cached before render.
 */
export async function prefetchFieldMetadata(objectName: string): Promise<void> {
  const gobodoKey = SF_TO_GOBODO_KEY[objectName];
  if (!gobodoKey || runtimeCache[gobodoKey]) return;

  if (!fetchPromises[gobodoKey]) {
    fetchPromises[gobodoKey] = fetchFieldsFromGobodo(gobodoKey).then(result => {
      if (result) runtimeCache[gobodoKey] = result;
      return result;
    });
  }

  await fetchPromises[gobodoKey];
}

/**
 * List all available object names in the metadata.
 */
export function getAvailableObjects(): string[] {
  return OBJECT_MAPPINGS.map(([sfObject]) => sfObject);
}

/**
 * Metadata generation info.
 */
export function getMetadataInfo(): { timestamp: string; org: string } {
  return {
    timestamp: new Date().toISOString(),
    org: process.env.REACT_APP_GOBODO_API_URL ?? process.env.GOBODO_API_URL ?? 'gobodo-runtime',
  };
}
